import React, { useState, useEffect, useMemo } from 'react';
import { IoIosArrowDown, IoMdClose } from 'react-icons/io';
import { IoMdEye, IoMdEyeOff } from 'react-icons/io';
import toast from 'react-hot-toast';
import { FiRefreshCw } from 'react-icons/fi';

import { API_URL } from '../../services/api.js';
import { DataState } from '../../context/DataProvider.js';

const GohighlevelResourceModal = ({ onClose }) => {
  const { userGoogleId } = DataState();

  const [accountInfo, setAccountInfo] = useState(null);
  const [subaccountsList, setSubaccountsList] = useState([]);
  const [selectedSubResourceKeys, setSelectedSubResourceKeys] = useState(
    new Set(),
  );

  const [searchTerm, setSearchTerm] = useState('');

  const [loading, setLoading] = useState(true);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastRefreshed, setLastRefreshed] = useState(null);

  const [expandedId, setExpandedId] = useState(null);

  const [showCompanyId, setShowCompanyId] = useState(false);
  const [showLocationId, setShowLocationId] = useState(false);
  const [showUserId, setShowUserId] = useState(false);

  const processFetchedData = (data) => {
    const account = data.account_info;

    setAccountInfo({
      userType: account.userType,
      companyId: account.companyId,
      locationId: account.locationId || null,
      userId: account.userId,
      approveAllLocations: account.approveAllLocations,
      restrictSubAccount: account.restrictSubAccount,
    });

    const subaccounts = account.subaccounts || [];
    const selectedIds = new Set(account.selectedSubaccounts || []);

    setSubaccountsList(subaccounts);
    setSelectedSubResourceKeys(selectedIds);
    setLastRefreshed(data.timestamp || null);
  };

  const fetchResources = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/gohighlevel/resources`, {
        method: 'GET',
        headers: { Authorization: `Bearer ${userGoogleId}` },
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Fetch failed');

      const json = await res.json();
      if (!json.success) throw new Error(json.message || 'Fetch failed');

      processFetchedData(json.data);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchResources();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    toast.loading('Refreshing...');

    try {
      const res = await fetch(`${API_URL}/gohighlevel/resources/refresh`, {
        method: 'GET',
        headers: { Authorization: `Bearer ${userGoogleId}` },
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Refresh failed');

      const json = await res.json();
      if (!json.success) throw new Error(json.message || 'Refresh failed');

      processFetchedData(json.data);

      toast.dismiss();
      toast.success('Resources refreshed');
    } catch (err) {
      toast.dismiss();
      toast.error(err.message);
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleToggleSubResource = (id) => {
    setSelectedSubResourceKeys((prev) => {
      const updated = new Set(prev);

      const willBeSelected = !updated.has(id);

      if (willBeSelected) {
        updated.add(id);
        setExpandedId(id);
      } else {
        updated.delete(id);
        if (expandedId === id) setExpandedId(null);
      }

      return updated;
    });
  };

  const filteredSubaccounts = useMemo(() => {
    return subaccountsList.filter((sub) =>
      sub.name.toLowerCase().includes(searchTerm.toLowerCase()),
    );
  }, [subaccountsList, searchTerm]);

  const handleSelectAll = () => {
    const allIds = filteredSubaccounts.map((sub) => sub.id);
    setSelectedSubResourceKeys(new Set(allIds));
  };

  const handleDeselectAll = () => {
    setSelectedSubResourceKeys(new Set());
  };

  const handleConfirmSelection = async () => {
    setConfirmLoading(true);
    try {
      const payload = {
        selectedSubaccounts: Array.from(selectedSubResourceKeys),
      };

      const res = await fetch(`${API_URL}/gohighlevel/resources/select`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${userGoogleId}`,
        },
        body: JSON.stringify(payload),
        credentials: 'include',
      });

      if (!res.ok) throw new Error('Failed to save selection');

      toast.success('Configuration saved!');
      onClose();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setConfirmLoading(false);
    }
  };

  const RevealField = ({ label, value, show, toggle }) => (
    <p className="flex items-center gap-3">
      <span className="font-medium">{label}:</span>

      {show ? (
        <button
          onClick={toggle}
          className="flex items-center gap-2 rounded-full bg-black bg-opacity-60 px-3 py-1 text-xs text-zinc-400 transition hover:bg-zinc-900"
        >
          {value}
          <IoMdEyeOff />
        </button>
      ) : (
        <button
          onClick={toggle}
          className="flex items-center gap-2 rounded-full bg-black bg-opacity-60 px-3 py-1 text-xs text-zinc-400 transition hover:bg-zinc-900"
        >
          Click to reveal
          <IoMdEye />
        </button>
      )}
    </p>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 p-4">
      <div className="bg-2 flex max-h-[90%] w-full max-w-xl flex-col rounded-lg border border-zinc-700 p-6">
        {/* Header */}
        <div className="mb-4 flex items-start justify-between">
          <div>
            <h2 className="text-lg font-semibold text-zinc-200">
              GoHighLevel Configuration
            </h2>
            {lastRefreshed && (
              <p className="text-xs text-zinc-400">
                Last updated: {new Date(lastRefreshed).toLocaleString()}
              </p>
            )}
          </div>

          <div className="flex items-center">
            <button
              onClick={handleRefresh}
              disabled={isRefreshing}
              className="mr-2 p-2 text-zinc-400 hover:text-white disabled:opacity-50"
            >
              <FiRefreshCw className={isRefreshing ? 'animate-spin' : ''} />
            </button>

            <button
              onClick={onClose}
              className="text-zinc-500 hover:text-white"
            >
              <IoMdClose size={22} />
            </button>
          </div>
        </div>

        {/* Account Info */}
        {accountInfo && (
          <div className="mb-4 space-y-2 rounded-lg border border-zinc-700 bg-zinc-800 p-4 text-sm">
            {accountInfo.userType === 'Company' ? (
              <>
                <RevealField
                  label="Company ID"
                  value={accountInfo.companyId}
                  show={showCompanyId}
                  toggle={() => setShowCompanyId((prev) => !prev)}
                />

                <RevealField
                  label="User ID"
                  value={accountInfo.userId}
                  show={showUserId}
                  toggle={() => setShowUserId((prev) => !prev)}
                />

                <p>User Type: {accountInfo.userType}</p>
                <p className="text-xs text-zinc-400">
                  Approve All: {accountInfo.approveAllLocations ? 'Yes' : 'No'}{' '}
                  | Restrict Subaccount:{' '}
                  {accountInfo.restrictSubAccount ? 'Yes' : 'No'}
                </p>
              </>
            ) : (
              <>
                <RevealField
                  label="Location ID"
                  value={accountInfo.locationId}
                  show={showLocationId}
                  toggle={() => setShowLocationId((prev) => !prev)}
                />

                <RevealField
                  label="Company ID"
                  value={accountInfo.companyId}
                  show={showCompanyId}
                  toggle={() => setShowCompanyId((prev) => !prev)}
                />

                <RevealField
                  label="User ID"
                  value={accountInfo.userId}
                  show={showUserId}
                  toggle={() => setShowUserId((prev) => !prev)}
                />

                <p>User Type: {accountInfo.userType}</p>
              </>
            )}
          </div>
        )}

        {/* Search Bar */}
        {accountInfo?.userType === 'Company' && (
          <div className="mb-3 flex flex-col gap-2 sm:flex-row">
            <div className="relative flex-1">
              <input
                type="text"
                placeholder="Search subaccounts..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full rounded border border-zinc-700 bg-zinc-900 px-3 py-2 pr-8 text-sm text-zinc-200"
              />

              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white"
                >
                  <IoMdClose />
                </button>
              )}
            </div>

            <div className="flex gap-2 sm:justify-end">
              <button
                onClick={handleSelectAll}
                className="flex-1 rounded border border-zinc-600 bg-zinc-800 px-3 py-2 text-xs sm:flex-none"
              >
                Select All
              </button>

              <button
                onClick={handleDeselectAll}
                className="flex-1 rounded border border-zinc-600 bg-zinc-800 px-3 py-2 text-xs sm:flex-none"
              >
                Clear
              </button>
            </div>
          </div>
        )}

        {/* Subaccounts List */}
        {accountInfo?.userType === 'Company' && (
          <div className="flex-grow space-y-3 overflow-y-auto">
            {!loading &&
              filteredSubaccounts.map((sub) => {
                const isExpanded = expandedId === sub.id;
                const isChecked = selectedSubResourceKeys.has(sub.id);

                return (
                  <div
                    key={sub.id}
                    className="rounded-lg border border-zinc-700"
                  >
                    {/* eslint-disable-next-line */}
                    <div
                      className={`flex cursor-pointer items-center justify-between p-3 ${isChecked ? 'bg-zinc-800' : 'bg-zinc-900'}`}
                      onClick={() =>
                        setExpandedId((prev) =>
                          prev === sub.id ? null : sub.id,
                        )
                      }
                    >
                      <div className="flex items-center gap-3">
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleToggleSubResource(sub.id);
                          }}
                        />
                        {/* eslint-disable-next-line */}
                        <span
                          className="cursor-pointer"
                          onClick={(e) => {
                            e.stopPropagation();
                            setExpandedId((prev) =>
                              prev === sub.id ? null : sub.id,
                            );
                          }}
                        >
                          {sub.name}
                        </span>
                      </div>

                      <IoIosArrowDown
                        className={`transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                      />
                    </div>

                    {isExpanded && (
                      <div className="space-y-1 bg-zinc-800 p-4 text-sm text-zinc-300">
                        <p>Email: {sub.email || 'N/A'}</p>
                        <p>Phone: {sub.phone || 'N/A'}</p>
                        <p>
                          City: {sub.city}, {sub.state}, {sub.country}
                        </p>
                        <p>Timezone: {sub.timezone}</p>
                      </div>
                    )}
                  </div>
                );
              })}
          </div>
        )}
        {loading && <p className="text-center text-zinc-400">Loading...</p>}

        {/* Footer */}
        <div className="mt-6 flex justify-end border-t border-zinc-700 pt-4">
          <button
            onClick={handleConfirmSelection}
            disabled={confirmLoading}
            className="rounded bg-blue-600 px-4 py-2 text-white hover:bg-blue-700"
          >
            {confirmLoading ? 'Saving...' : 'Save Configuration'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default GohighlevelResourceModal;
